'use client';

import type { ComponentType } from 'react';
import { clsx } from 'clsx';
import FadeInSection from '@/components/ui/FadeInSection';
import ParallaxDrift from '@/components/ui/ParallaxDrift';
import { StageMarker } from '@/components/layout/StoryRail';
import { SmartImage } from '@/components/ui/Skeleton';
import ImageComparisonSlider from '@/components/ui/ImageComparisonSlider';
import MediaCarousel from '@/components/ui/MediaCarousel';
import PhotoGallery from '@/components/ui/PhotoGallery';
import PairGallery from '@/components/ui/PairGallery';
import DragGallery from '@/components/ui/DragGallery';
import VideoBlock from '@/components/ui/VideoBlock';

export interface Benefit {
  title: string;
  desc: string;
  /** Per-benefit line icon; falls back to the generic check when omitted. */
  icon?: ComponentType<{ className?: string }>;
}

export interface Comparison {
  left: string;
  right: string;
}

interface ServiceSectionProps {
  id: string;
  eyebrow: string;
  headline: string;
  benefits: Benefit[];
  /** Photos shown as a one-at-a-time carousel. */
  images?: string[];
  /** Photos shown as a bento grid that opens a lightbox (Painting/walls). */
  gallery?: string[];
  /** Two photos shown as an equal side-by-side pair that opens a lightbox (Floors & Doors). */
  pairGallery?: string[];
  /** Photos in a drag/swipe-scroll strip that opens a lightbox (Tile). */
  dragGallery?: string[];
  /** Clips shown as a one-at-a-time carousel. */
  videos?: string[];
  /** Before/after slider — lead visual when provided. */
  comparison?: Comparison;
  /** A single still shown beside the comparison slider (Electrical). */
  image?: string;
  /** Side the media block sits on at desktop. Alternates per section. */
  mediaPosition?: 'left' | 'right';
  /** Background tone — 'light' sections get a faint tint so adjacent sections
      read as gently separated (paired with a hairline top divider). */
  tone?: 'dark' | 'light';
  /** Build-stage number (1–4) shown as a marker on the story rail. The four
      trades run in real renovation order, matching the staged payments. */
  stage?: number;
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
      aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ServiceMedia({
  images = [],
  gallery = [],
  pairGallery = [],
  dragGallery = [],
  videos = [],
  comparison,
  image,
  eyebrow,
}: {
  images?: string[];
  gallery?: string[];
  pairGallery?: string[];
  dragGallery?: string[];
  videos?: string[];
  comparison?: Comparison;
  image?: string;
  eyebrow: string;
}) {
  return (
    <div className="space-y-6">
      {/* Drag/swipe-scroll strip + lightbox (Tile) */}
      {dragGallery.length > 0 && <DragGallery images={dragGallery} label={eyebrow} />}

      {/* Bento gallery + lightbox (Painting/walls) */}
      {gallery.length > 0 && <PhotoGallery images={gallery} label={eyebrow} />}

      {/* Equal side-by-side pair + lightbox (Floors & Doors) */}
      {pairGallery.length > 0 && <PairGallery images={pairGallery} label={eyebrow} />}

      {/* Before/after slider (Electrical) */}
      {comparison && (
        <div className="forest-img-shadow relative aspect-[4/3] overflow-hidden rounded-sm border border-surface-bright/20">
          <ImageComparisonSlider
            leftImage={comparison.left}
            rightImage={comparison.right}
            altLeft={eyebrow}
            altRight={eyebrow}
            autoSweep
          />
        </div>
      )}

      {/* Single still beside the slider (Electrical) — Ken Burns, not the slider */}
      {image && (
        <div className="forest-img-shadow relative aspect-[4/3] overflow-hidden rounded-sm border border-surface-bright/20">
          <SmartImage
            src={image}
            alt={eyebrow}
            fill
            sizes="(max-width: 1024px) 100vw, 55vw"
            className="ken-burns object-cover"
          />
        </div>
      )}

      {/* Photo carousel (Tile, Painting) */}
      {images.length > 0 && (
        <MediaCarousel
          slides={images.map((src) => ({ type: 'image' as const, src }))}
          label={eyebrow}
        />
      )}

      {/* Standalone video block below the images — posters with a play icon that
          open in the shared viewer (no autoplay). Stays a separate block (Tile). */}
      {videos.length > 0 && <VideoBlock videos={videos} label={eyebrow} />}
    </div>
  );
}

export default function ServiceSection({
  id,
  eyebrow,
  headline,
  benefits,
  images = [],
  gallery = [],
  pairGallery = [],
  dragGallery = [],
  videos = [],
  comparison,
  image,
  mediaPosition = 'right',
  tone = 'dark',
  stage,
}: ServiceSectionProps) {
  const mediaFirst = mediaPosition === 'left';
  const hasMedia = Boolean(
    comparison ||
      image ||
      images.length > 0 ||
      gallery.length > 0 ||
      pairGallery.length > 0 ||
      dragGallery.length > 0 ||
      videos.length > 0
  );

  return (
    <section
      id={id}
      // A faint top hairline divides sections, and 'light'-tone sections get a
      // very subtle tint, so adjacent sections read as gently separated over the
      // shared atmosphere (without the heavy bands of the old design).
      className={clsx(
        'relative scroll-mt-2 border-t border-surface-bright/15 px-margin-mobile py-section-gap lg:scroll-mt-4 lg:px-margin-desktop',
        tone === 'light' && 'bg-surface-bright/5'
      )}
    >
      {stage !== undefined && <StageMarker n={stage} />}
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-stack-lg lg:grid-cols-12 lg:gap-16">
        {/* Text */}
        <div
          className={clsx(
            'min-w-0',
            hasMedia ? 'lg:col-span-5' : 'lg:col-span-12',
            mediaFirst ? 'lg:order-2' : 'lg:order-1'
          )}
        >
          {/* Heading slides in from the left, the benefits from the right. */}
          <FadeInSection direction="right" distance={64}>
            <p className="text-label-caps uppercase tracking-widest text-primary">{eyebrow}</p>
            <h2 className="mt-stack-md font-headline text-headline-lg font-bold tracking-[0.02em] text-on-surface">
              {headline}
            </h2>
          </FadeInSection>

          <FadeInSection direction="left" distance={64} delay={0.15} className="mt-stack-lg">
            <ul className="space-y-stack-md">
              {benefits.map((b) => {
                const Icon = b.icon ?? CheckIcon;
                return (
                  <li key={b.title} className="flex gap-stack-md">
                    <Icon className="mt-0.5 shrink-0 text-primary" />
                    <div>
                      <h3 className="font-headline text-headline-md font-semibold text-on-surface">{b.title}</h3>
                      <p className="mt-1 text-body-md text-on-surface-variant">{b.desc}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </FadeInSection>
        </div>

        {/* Media */}
        {hasMedia && (
          <FadeInSection
            direction={mediaFirst ? 'right' : 'left'}
            delay={0.1}
            className={clsx(
              'min-w-0',
              'lg:col-span-7',
              mediaFirst ? 'lg:order-1' : 'lg:order-2'
            )}
          >
            {/* Media plane drifts slightly slower than the text — quiet depth */}
            <ParallaxDrift>
              <ServiceMedia
                images={images}
                gallery={gallery}
                pairGallery={pairGallery}
                dragGallery={dragGallery}
                videos={videos}
                comparison={comparison}
                image={image}
                eyebrow={eyebrow}
              />
            </ParallaxDrift>
          </FadeInSection>
        )}
      </div>
    </section>
  );
}
